'use client'

import type { ChatAttachment } from '@/lib/ai/types'

function fmt(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'number') return v.toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/**
 * "chart" attachment — no chart library in the chat panel; render the data as
 * a compact list so nothing is lost.
 */
export function ChartFallback({ attachment }: { attachment: ChatAttachment }) {
  const data = attachment.data ?? {}
  const series = (data.series ?? data.rows ?? data.data ?? data.points) as unknown
  let items: Array<{ label: string; value: string }> = []
  if (Array.isArray(series)) {
    items = series.slice(0, 20).map((row, i) => {
      if (row && typeof row === 'object') {
        const r = row as Record<string, unknown>
        const label = r.label ?? r.name ?? r.key ?? r.month ?? r.x ?? `#${i + 1}`
        const value = r.value ?? r.total ?? r.count ?? r.amount ?? r.y
        return { label: String(label), value: fmt(value) }
      }
      return { label: `#${i + 1}`, value: fmt(row) }
    })
  } else if (data && typeof data === 'object') {
    items = Object.entries(data)
      .filter(([, v]) => typeof v !== 'object')
      .slice(0, 20)
      .map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: fmt(v) }))
  }

  return (
    <div className="rounded-xl border border-border/50 bg-white p-3 shadow-soft">
      <p className="text-xs font-semibold text-brand-navy">{attachment.title}</p>
      {attachment.description && <p className="mt-0.5 text-xs text-muted-foreground">{attachment.description}</p>}
      {items.length > 0 && (
        <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-xs">
          {items.map((it, i) => (
            <div key={i} className="contents">
              <dt className="truncate text-muted-foreground">{it.label}</dt>
              <dd className="text-right font-medium text-foreground">{it.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
